import type { LegHint } from "./leg-hint";
import { LegHintKind } from "./leg-hint";

export type HintGroup = {
  kind?: LegHintKind;
  hintMessage?: LegHint["hintMessage"];
  legIds: string[];
};

export type LegHintContext = {
  legId: string;
  hint?: LegHint;
  scope?: string;
};

const SORT_PRIORITY: Record<LegHintKind, number> = {
  [LegHintKind.NotCombinable]: 0,
  [LegHintKind.BetBuilderNotCombinable]: 0,
  [LegHintKind.MarketClosed]: 2,
  [LegHintKind.MarketSuspended]: 3,
  [LegHintKind.InvalidSGMCombination]: 4,
};

const NO_HINT_PRIORITY = 100;

const getGroupKey = ({ hint, scope }: LegHintContext): string | undefined => {
  if (!hint) return undefined;

  return `${hint.kind}::${scope ?? ""}`;
};

const getHintGroupPriority = (group: HintGroup): number => (group.kind ? SORT_PRIORITY[group.kind] : NO_HINT_PRIORITY);

const createHintGroup = ({ legId, hint }: LegHintContext): HintGroup =>
  hint ? { kind: hint.kind, hintMessage: hint.hintMessage, legIds: [legId] } : { legIds: [legId] };

export const buildHintGroups = (contexts: LegHintContext[]): HintGroup[] => {
  const groupsByKey = new Map<string | undefined, HintGroup>();

  const groups = contexts.reduce<HintGroup[]>((acc, context) => {
    const key = getGroupKey(context);
    const existing = groupsByKey.get(key);

    if (existing) {
      existing.legIds.push(context.legId);
      return acc;
    }

    const group = createHintGroup(context);
    groupsByKey.set(key, group);
    return [...acc, group];
  }, []);

  return groups.sort((a, b) => getHintGroupPriority(a) - getHintGroupPriority(b));
};
